import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CountBadgePipe } from 'app/core/pipes/badge.pipe';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

@Component({
  selector: 'app-survey-overview',
  standalone: true,
  imports: [NgClass, DatePipe, CountBadgePipe, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './survey-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyOverviewComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() widgetId = 'surveySummary';
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  data: any = null;
  loading = false;
  error: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.dashboardType) this.fetchData();
  }

  private fetchData(): void {
    this.loading = true;
    this.error = null;
    this.engine
      .getWidgetData(this.dashboardType, this.widgetId, this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.data = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getParticipationColor(pct: number): string {
    if (pct >= 75) return 'bg-green-500';
    if (pct >= 40) return 'bg-yellow-400';
    return 'bg-red-400';
  }

  getDaysUntilExpiry(expireAt: string): number {
    if (!expireAt) return 0;
    const diff = new Date(expireAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  navigateToSurvey(id: number): void {
    this.router.navigate(['/communication/surveys'], { queryParams: { id } });
  }

  navigateToAll(): void {
    this.router.navigate(['/communication/surveys']);
  }
}
